import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getDealDetail } from "@/lib/deals.functions";
import { DealPageHeader } from "@/components/deal-page-header";
import { DealNav } from "@/components/deal-nav";
import { DeckStudioTileSection } from "@/components/deck-studio/tiles";
import { FinModelReviewCard } from "@/components/fin-model-review-card";
import { RaiseStrategyCard } from "@/components/raise-strategy-card";
import { DealUpdateCard } from "@/components/deal-update-card";
import { MarketCompsCard } from "@/components/market-comps-card";
import { TermSheetCard } from "@/components/term-sheet-card";
import { cn } from "@/lib/utils";
import {
  listAllDealReports,
  type DealReportProduct,
  type DealReportRow,
} from "@/lib/deal-reports.functions";

export const Route = createFileRoute("/_authed/deals/$dealId_/reports")({
  validateSearch: (search: Record<string, unknown>) => {
    const raw = String(search.product ?? "");
    const product: DealReportProduct | undefined = PRODUCT_KEYS.includes(
      raw as DealReportProduct,
    )
      ? (raw as DealReportProduct)
      : undefined;
    return { product };
  },
  head: () => ({
    meta: [
      { title: "Deal reports: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: DealReportsPage,
});

const PRODUCTS: Array<{ key: DealReportProduct; label: string }> = [
  { key: "investor_readiness", label: "Investor Readiness" },
  { key: "model_review", label: "Financial Model Review" },
  { key: "raise_strategy", label: "Raise Strategy" },
  { key: "deal_update", label: "Deal Update" },
  { key: "market_comps", label: "Market Comps" },
  { key: "term_sheet", label: "Term Sheet" },
];

const PRODUCT_KEYS: DealReportProduct[] = [
  "investor_readiness",
  "model_review",
  "raise_strategy",
  "deal_update",
  "market_comps",
  "term_sheet",
];

function statusPill(status: string): { label: string; cls: string } {
  if (status === "complete")
    return {
      label: "Complete",
      cls: "border-brand-ok/30 bg-brand-ok/10 text-[oklch(0.45_0.087_155.7)]",
    };
  if (status === "processing")
    return {
      label: "Processing",
      cls: "bg-brand-processing text-white animate-[pulse-processing_1.5s_ease-in-out_infinite]",
    };
  if (status === "failed")
    return {
      label: "Failed",
      cls: "border-destructive/30 bg-destructive/10 text-destructive",
    };
  return { label: status, cls: "border-brand-line bg-brand-paper-2 text-brand-muted" };
}

function fmt(d: string | null): string {
  if (!d) return "not yet";
  return new Date(d).toLocaleDateString();
}

function ReportLink({
  dealId,
  row,
  children,
}: {
  dealId: string;
  row: DealReportRow;
  children: React.ReactNode;
}) {
  const cls = "text-[14px] underline underline-offset-2";
  if (row.product === "investor_readiness")
    return (
      <Link
        to="/deals/$dealId/deck-studio"
        params={{ dealId }}
        search={{ version: row.version_number } as never}
        className={cls}
      >
        {children}
      </Link>
    );
  if (row.product === "model_review")
    return (
      <Link
        to="/deals/$dealId/model-review/$reportId"
        params={{ dealId, reportId: row.id }}
        className={cls}
      >
        {children}
      </Link>
    );
  if (row.product === "raise_strategy")
    return (
      <Link
        to="/deals/$dealId/raise-strategy/$reportId"
        params={{ dealId, reportId: row.id }}
        className={cls}
      >
        {children}
      </Link>
    );
  if (row.product === "term_sheet")
    return (
      <Link
        to="/deals/$dealId/term-sheet/$reportId"
        params={{ dealId, reportId: row.id }}
        className={cls}
      >
        {children}
      </Link>
    );
  if (row.product === "market_comps")
    return (
      <Link
        to="/deals/$dealId/market-comps/$reportId"
        params={{ dealId, reportId: row.id }}
        className={cls}
      >
        {children}
      </Link>
    );
  return (
    <Link
      to="/deals/$dealId/deal-update/$reportId"
      params={{ dealId, reportId: row.id }}
      className={cls}
    >
      {children}
    </Link>
  );
}

function DealReportsPage() {
  const { dealId } = Route.useParams();
  const product = Route.useSearch().product ?? "investor_readiness";
  const navigate = useNavigate();
  const fetchReports = useServerFn(listAllDealReports);
  const query = useQuery({
    queryKey: ["deal-reports-index", dealId],
    queryFn: () => fetchReports({ data: { dealId } }),
  });
  const rows = query.data?.reports ?? [];
  const items = rows
    .filter((r) => r.product === product)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));

  const fetchDetail = useServerFn(getDealDetail);
  const detail = useQuery({
    queryKey: ["deal-detail", dealId],
    queryFn: () => fetchDetail({ data: { dealId } }),
  });
  const parseReport = Array.isArray(
    (detail.data?.deal as { parse_report?: unknown } | undefined)?.parse_report,
  )
    ? ((detail.data!.deal as { parse_report: unknown[] })
        .parse_report as Array<{ category?: string }>).filter(
        (e) =>
          e &&
          (e.category === "presentation" ||
            e.category === "memo" ||
            e.category === "template"),
      )
    : [];

  return (
    <main className="mx-auto max-w-6xl px-6 py-10 space-y-6">
      <DealPageHeader dealId={dealId} />
      <DealNav
        dealId={dealId}
        active="reports"
        isDemo={(detail.data?.deal as { is_demo?: boolean } | undefined)?.is_demo === true}
      />

      <nav
        aria-label="Report products"
        className="flex flex-wrap items-center gap-1 border-b border-brand-line pb-3"
      >
        {PRODUCTS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            aria-current={product === key ? "page" : undefined}
            onClick={() =>
              navigate({
                to: "/deals/$dealId/reports",
                params: { dealId },
                search: { product: key },
              })
            }
            className={cn(
              "rounded-full px-3.5 py-1.5 text-[15px] transition-colors",
              product === key
                ? "bg-brand-ink text-brand-paper"
                : "text-brand-muted hover:bg-brand-paper-2 hover:text-brand-ink",
            )}
          >
            {label}
          </button>
        ))}
      </nav>

      {product === "investor_readiness" && (
        <DeckStudioTileSection
          dealId={dealId}
          hasUploads={parseReport.length > 0}
          variant="readiness"
        />
      )}
      {product === "model_review" && <FinModelReviewCard dealId={dealId} />}
      {product === "raise_strategy" && <RaiseStrategyCard dealId={dealId} />}
      {product === "deal_update" && <DealUpdateCard dealId={dealId} />}
      {product === "market_comps" && <MarketCompsCard dealId={dealId} />}
      {product === "term_sheet" && <TermSheetCard dealId={dealId} />}

      {query.isLoading && (
        <p className="text-[15px] text-brand-muted">Loading reports.</p>
      )}
      {query.isError && (
        <p className="text-[15px] text-destructive">
          We could not load your reports. Please refresh and try again.
        </p>
      )}

      {!query.isLoading && !query.isError && (
        <section className="space-y-2">
          <h2 className="text-[15px] font-semibold uppercase tracking-wider text-brand-muted">
            Version history
          </h2>
          {items.length === 0 ? (
            <p className="text-[15px] text-brand-muted">None yet.</p>
          ) : (
                  <ul className="divide-y divide-brand-line rounded-2xl border border-brand-line bg-brand-paper">
                    {items.map((r) => (
                      <li
                        key={`${r.product}-${r.id}`}
                        className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                      >
                        <div>
                          <ReportLink dealId={dealId} row={r}>
                            Version {r.version_number}
                          </ReportLink>
                          <p className="mt-0.5 text-[12px] text-brand-muted">
                            Created {fmt(r.created_at)} · Completed{" "}
                            {fmt(r.completed_at)}
                          </p>
                        </div>
                        <span
                          className={`rounded-full border px-2 py-0.5 text-[12px] font-medium ${statusPill(r.report_status).cls}`}
                        >
                          {statusPill(r.report_status).label}
                        </span>
                      </li>
                    ))}
                  </ul>
          )}
        </section>
      )}
    </main>
  );
}
